import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  usePackagePresets,
  useCreatePackagePreset,
  useUpdatePackagePreset,
  useDeletePackagePreset,
} from '../hooks/useShippingLabels';
import type { PackagePreset, PackagePresetInput } from '../types';

interface PackagePresetsManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const UNIT_OPTIONS = ['cm', 'in'];

interface PresetFormState {
  label: string;
  length: string;
  width: string;
  height: string;
  unit: string;
  defaultWeight: string;
}

const EMPTY_FORM: PresetFormState = {
  label: '',
  length: '',
  width: '',
  height: '',
  unit: 'cm',
  defaultWeight: '',
};

function toInput(form: PresetFormState): PackagePresetInput {
  return {
    label: form.label.trim(),
    length: form.length === '' ? null : Number(form.length),
    width: form.width === '' ? null : Number(form.width),
    height: form.height === '' ? null : Number(form.height),
    unit: form.unit,
    defaultWeight: form.defaultWeight === '' ? null : Number(form.defaultWeight),
  };
}

function fromPreset(preset: PackagePreset): PresetFormState {
  return {
    label: preset.label,
    length: preset.length == null ? '' : String(preset.length),
    width: preset.width == null ? '' : String(preset.width),
    height: preset.height == null ? '' : String(preset.height),
    unit: preset.unit || 'cm',
    defaultWeight: preset.defaultWeight == null ? '' : String(preset.defaultWeight),
  };
}

function isValidNumberField(value: string): boolean {
  return value === '' || !Number.isNaN(Number(value));
}

function isPresentNumberField(value: string): boolean {
  return value !== '' && !Number.isNaN(Number(value));
}

function formatDimensions(preset: PackagePreset, unitLabels: Record<string, string>): string | null {
  if (preset.length == null && preset.width == null && preset.height == null) return null;
  const l = preset.length ?? '-';
  const w = preset.width ?? '-';
  const h = preset.height ?? '-';
  const unit = unitLabels[preset.unit] ?? preset.unit;
  return `${l}×${w}×${h} ${unit}`;
}

export function PackagePresetsManager({ open, onOpenChange }: PackagePresetsManagerProps) {
  const { t } = useTranslation('planete-courrier');
  const { data: presets, isLoading } = usePackagePresets();
  const createPreset = useCreatePackagePreset();
  const updatePreset = useUpdatePackagePreset();
  const deletePreset = useDeletePackagePreset();

  const unitLabels: Record<string, string> = {
    cm: t('presets.unitCm'),
    in: t('presets.unitIn'),
  };

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PresetFormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<PackagePreset | null>(null);

  const isEditing = editingId !== null;
  const isSaving = createPreset.isPending || updatePreset.isPending;
  const canSubmit =
    form.label.trim().length > 0 &&
    isPresentNumberField(form.length) &&
    isPresentNumberField(form.width) &&
    isPresentNumberField(form.height) &&
    isValidNumberField(form.defaultWeight);

  const openAddForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEditForm = (preset: PackagePreset) => {
    setEditingId(preset.id);
    setForm(fromPreset(preset));
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const data = toInput(form);
    if (isEditing && editingId) {
      updatePreset.mutate({ id: editingId, data }, { onSuccess: closeForm });
    } else {
      createPreset.mutate(data, { onSuccess: closeForm });
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deletePreset.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  };

  const dialogTitle = formOpen
    ? (isEditing ? t('presets.edit') : t('presets.add'))
    : t('presets.title');

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            closeForm();
            setDeleteTarget(null);
          }
          onOpenChange(next);
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>

          {formOpen ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="preset-label">{t('presets.label')}</Label>
                <Input
                  id="preset-label"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="preset-length">{t('presets.length')}</Label>
                  <Input
                    id="preset-length"
                    type="number"
                    inputMode="decimal"
                    value={form.length}
                    onChange={(e) => setForm({ ...form, length: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preset-width">{t('presets.width')}</Label>
                  <Input
                    id="preset-width"
                    type="number"
                    inputMode="decimal"
                    value={form.width}
                    onChange={(e) => setForm({ ...form, width: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preset-height">{t('presets.height')}</Label>
                  <Input
                    id="preset-height"
                    type="number"
                    inputMode="decimal"
                    value={form.height}
                    onChange={(e) => setForm({ ...form, height: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="preset-unit">{t('presets.unit')}</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger id="preset-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unitLabels[unit] ?? unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preset-weight">{t('presets.defaultWeight')}</Label>
                  <Input
                    id="preset-weight"
                    type="number"
                    inputMode="decimal"
                    value={form.defaultWeight}
                    onChange={(e) => setForm({ ...form, defaultWeight: e.target.value })}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeForm}>
                  {t('presets.cancel')}
                </Button>
                <Button type="submit" disabled={!canSubmit || isSaving}>
                  {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  {t('presets.save')}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button type="button" size="sm" onClick={openAddForm}>
                  <Plus className="h-4 w-4 mr-1" />
                  {t('presets.add')}
                </Button>
              </div>

              {isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : !presets || presets.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">{t('presets.empty')}</p>
              ) : (
                <ul className="space-y-2">
                  {presets.map((preset) => (
                    <li
                      key={preset.id}
                      className="flex items-center justify-between rounded-md border p-3"
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate">{preset.label}</div>
                        <div className="text-sm text-muted-foreground">
                          {formatDimensions(preset, unitLabels)}
                          {preset.defaultWeight != null && <span> &middot; {preset.defaultWeight}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t('presets.edit')}
                          onClick={() => openEditForm(preset)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t('presets.delete')}
                          onClick={() => setDeleteTarget(preset)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget !== null} onOpenChange={(next) => !next && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('presets.delete')}</AlertDialogTitle>
            <AlertDialogDescription>{t('presets.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePreset.isPending}>{t('presets.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteConfirm();
              }}
              disabled={deletePreset.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletePreset.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('presets.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
